import { gql } from "@uplift-ltd/apollo";
import { Course } from "./Catalog/CourseCatalog";
import { AUTHOR_INFO_FRAGMENT } from "./fragments";

export type { CourseViewUserQueryCouponForUser as CouponForUser } from "./__generated__/queries";

const SHOW_CE_UNITS = {
  min: 1,
  max: 18,
};

export const COURSE_VIEW_USER_QUERY = gql`
  query CourseViewUserQuery($id: ID!, $idType: CourseIdType!) {
    course(id: $id, idType: $idType) {
      id
      isEnrolled
      isStarted
      startedOn
      completedOn
      isTimeExpiredForUser
      timeExpiresForUserOn
      courseProgress
      product {
        id
        couponForUser {
          code
          discountedPriceUSD
        }
        userDiscountsFromSubscription {
          id
          displayName
          percent
          fixedPrice
          subscriptionId
          productFeatureId
          usageLimit
          usageCount
          ceLimit
          ceCount
        }
        hasPurchased
        courses {
          id
          courseProgress
          isStarted
          startedOn
          completedOn
        }
      }
      event {
        id
        userEventDetails {
          certificateId
          isLiveCeAwardable
        }
      }
      lessons {
        id
        title
        content
      }
      quizzes {
        id
        questions {
          id
          title
          question
          isIncorrect
          answers {
            id
            title
            isMyAnswer
            isCorrect
            answerValue
            type
            essay
          }
        }
      }
    }
  }
`;

export const COURSE_VIEW_QUERY = gql`
  query CourseViewQuery($id: ID!, $idType: CourseIdType!) {
    course(id: $id, idType: $idType) {
      id
      content
      date
      ceUnits
      priceUSD
      subscriptionFeatureOptIns {
        membersExclusive
      }
      materials
      educationalObjectives
      syllabus
      fulfillsRequirements {
        edges {
          node {
            id
            name
            description
            slug
          }
        }
      }
      slug
      title
      isNew
      isExpired
      isRecentlyUpdated
      updatedOn
      courseFields {
        programCode
        displayDateBadges
        courseMaterialsExtraText
      }
      seo {
        title
        metaDesc
        metaKeywords
        canonical
        opengraphImage {
          id
          mediaItemUrl
        }
      }
      product {
        id
        sku
        databaseId
        priceUSD
        name
        canPurchaseWithCredits
        creditCost
        description
        type
        availability {
          isPurchasable
        }
        courses {
          id
          title
          slug
          ceUnits
          priceUSD
        }
        seo {
          title
          metaDesc
          metaKeywords
          canonical
          opengraphImage {
            id
            mediaItemUrl
          }
        }
      }
      event {
        id
      }
      authors {
        id
        ...AuthorInfoFragment
      }
    }
  }
  ${AUTHOR_INFO_FRAGMENT}
`;

export const filterDuplicateCourses = (courseA: Course, index: number, courses: Course[]) =>
  courses.findIndex(
    (courseB: Course) =>
      courseB.title === courseA.title &&
      courseB.priceUSD === courseA.priceUSD &&
      courseB.ceUnits === courseA.ceUnits
  ) === index;

/**
 * Filter courses within a certain range of CE units, nonzero price, and not containing "test".
 *
 * @param course a GraphQL course
 * @returns filtered list of courses
 */
export const filterCoursesForCatalog = (course: Course) =>
  course.ceUnits >= SHOW_CE_UNITS.min &&
  course.ceUnits <= SHOW_CE_UNITS.max &&
  course.priceUSD !== 0;
